import type { CalendarApi, DateInput } from '@fullcalendar/core';
import type FullCalendar from '@fullcalendar/react';
import { useToast } from '@src/app/notifications/toast';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ABS_DAY_VIEW,
  ABS_MONTH_VIEW,
  absenceSelectionToSlot,
  getAbsenceSelectionById,
  getFixedMonthVisibleRange,
  getFocusWindowStart,
  getSessionId,
  getUserWorktimeForDate,
  hydrateSelectedAbsence,
  prepareAbsencePayload,
  SCROLLGRID_SETTLE_MS,
  userResourceId,
} from '../../lib';
import {
  type Absence,
  type HrApproval,
  isAbsenceLockHeldStatus,
  type ManagerApproval,
  type SelectedAbsence,
  type SlotData,
  type TimelineSlot,
} from '../../types';
import { TimelineContext } from '../context';
import {
  getAbsenceQueryParams,
  getHolidayRangeQueryParams,
  getVacationBalanceQueryParams,
  invalidateAbsenceQueries,
  useAbsences,
  useHolidays,
  useResourceUsers,
  useVacationBalance,
} from '../query';

const GROUP_LABEL_SELECTOR = [
  '.fc-datagrid-cell-main [role="presentation"]',
  '.fc-datagrid-cell-main .MuiStack-root',
].join(', ');

export type CalendarController = {
  next: () => void;
  prev: () => void;
  today: () => void;
  getDate: () => Date | null;
  updateSize: () => void;
  select: (...args: Parameters<CalendarApi['select']>) => void;
  unselect: () => void;
  changeView: (...args: Parameters<CalendarApi['changeView']>) => void;
  gotoDate: (...args: Parameters<CalendarApi['gotoDate']>) => void;
  setMonthView: (year: number, month: number) => void;
  setMonthViewFrom: (startDate: Date, dayCount?: number) => void;
  setDayView: (date: DateInput) => void;
  setOption: (...args: Parameters<CalendarApi['setOption']>) => void;
  scrollToResource: (userId: number) => void;
  scheduleSettleResize: () => void;
};

/** Works a focused selection out again from a freshly loaded set of days. */
type RebuildSelection = (absences: Absence[]) => SelectedAbsence | null;

/** A focus waiting on the days that were off the edge of the window. */
type PendingRefocus = {
  rebuild: RebuildSelection;
  /** The days focused on, for telling a later selection from this one. */
  ids: number[];
};

export default function useTimeline() {
  const toast = useToast();
  const timelineContext = useContext(TimelineContext);
  const {
    view,
    selectedAbsence,
    setSelectedAbsence,
    selectedSlot,
    setSelectedSlot,
    sidebarMode,
    setSidebarMode,
    setSuppressSelectionUntil,
    selectedUserId,
    setSelectedUserId,
    closeEditForm,
    closeAbsenceDetails,
    calendarRef,
    visibleRange,
    absenceLock,
    showLegend,
    openLegend,
    closeLegend,
    showBalanceHistory,
    openBalanceHistory,
    closeBalanceHistory,
    ...restOfContext
  } = timelineContext;
  const absenceQueryParams = useMemo(
    () => getAbsenceQueryParams(visibleRange),
    [visibleRange],
  );
  const {
    absences,
    bookAbsence,
    deleteAbsences,
    requestAbsences,
    withdrawAbsences,
    approveWithdrawalAbsences,
    denyWithdrawalAbsences,
    denyAbsences,
    approveAbsences,
    approveHrAbsences,
    ...restAbsencesQuery
  } = useAbsences(absenceQueryParams);

  const [pendingEditSelection, setPendingEditSelection] =
    useState<SelectedAbsence | null>(null);
  // Which pane the pending selection should land in once its absences load.
  const [pendingEditMode, setPendingEditMode] = useState<
    'schedule' | 'withdraw'
  >('schedule');

  const {
    users,
    isFetching: isUsersFetching,
    isFetched: isUsersFetched,
  } = useResourceUsers();
  const vacationBalanceQueryParams = useMemo(
    () => ({ queryParams: getVacationBalanceQueryParams(visibleRange) }),
    [visibleRange],
  );
  const { balances: vacationBalances } = useVacationBalance(
    vacationBalanceQueryParams,
  );
  const holidayQueryParams = useMemo(
    () => getHolidayRangeQueryParams(visibleRange),
    [visibleRange],
  );
  const { holidays } = useHolidays(holidayQueryParams);

  const attachCalendar = useCallback(
    (instance: FullCalendar | null) => {
      calendarRef.current = instance;
    },
    [calendarRef],
  );

  const getApi = useCallback(
    () => calendarRef.current?.getApi() ?? null,
    [calendarRef],
  );

  const settleResizeRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (settleResizeRef.current !== null) {
        window.clearTimeout(settleResizeRef.current);
      }
    },
    [],
  );
  const calendar = useMemo<CalendarController>(() => {
    // A 31-day window starting at `startDate` unless a caller needs a longer
    // one -- a run that will not fit a month is shown in full and scrolled,
    // rather than cut off where somebody might decide on the part they can
    // see. The range is applied imperatively because the view is duration-free.
    const showMonthFrom = (startDate: Date, dayCount?: number) => {
      const { start, end } = getFixedMonthVisibleRange(startDate, dayCount);
      getApi()?.changeView(ABS_MONTH_VIEW, startDate);
      getApi()?.setOption('visibleRange', { start, end });
    };
    return {
      next: () => getApi()?.next(),
      prev: () => getApi()?.prev(),
      today: () => getApi()?.today(),
      getDate: () => getApi()?.getDate() ?? null,
      updateSize: () => getApi()?.updateSize(),
      select: (...args) => getApi()?.select(...args),
      unselect: () => getApi()?.unselect(),
      changeView: (...args) => getApi()?.changeView(...args),
      gotoDate: (...args) => getApi()?.gotoDate(...args),
      setMonthView: (year, month) => showMonthFrom(new Date(year, month, 1)),
      setMonthViewFrom: showMonthFrom,
      setDayView: (date) => getApi()?.changeView(ABS_DAY_VIEW, date),
      setOption: (...args) => getApi()?.setOption(...args),
      // Re-measure after the current burst of re-renders has died down, so the
      // final layout is not one of the passes ScrollGrid throttles away
      // (see SCROLLGRID_SETTLE_MS). Re-arming on every call means the resize
      // lands once the burst is actually over, not in the middle of it.
      scheduleSettleResize: () => {
        if (settleResizeRef.current !== null) {
          window.clearTimeout(settleResizeRef.current);
        }
        settleResizeRef.current = window.setTimeout(() => {
          settleResizeRef.current = null;
          // updateSize() can reset scrollTop when the content height shrank
          // (a collapsed group), which by now would look like a random jump.
          const scrollers = Array.from(
            document.querySelectorAll<HTMLElement>('.fc-scroller'),
          );
          const scrollTops = scrollers.map((scroller) => scroller.scrollTop);
          getApi()?.updateSize();
          scrollers.forEach((scroller, index) => {
            if (scroller.scrollTop !== scrollTops[index]) {
              scroller.scrollTop = scrollTops[index];
            }
          });
        }, SCROLLGRID_SETTLE_MS);
      },
      scrollToResource: (userId: number) => {
        const resourceId = userResourceId(userId);
        if (!resourceId) return;
        requestAnimationFrame(() => {
          const row = document.querySelector(
            `[data-resource-id="${resourceId}"]`,
          ) as HTMLElement | null;
          row?.scrollIntoView({
            block: 'center',
            behavior: 'smooth',
          });
        });
      },
    };
  }, [getApi]);

  const expandAndScrollToResource = useCallback(
    (userId: number) => {
      const resourceId = userResourceId(userId);
      if (!resourceId) return;

      const existsInDom = document.querySelector(
        `[data-resource-id="${resourceId}"]`,
      );

      if (existsInDom) {
        calendar.scrollToResource(userId);
        return;
      }

      const targetUser = users.find((u) => u.id === userId);
      const teamName = targetUser?.team?.name;
      if (!teamName) return;

      const groupRows = document.querySelectorAll('.absence-group-row');
      for (const groupCell of groupRows) {
        const label = groupCell.textContent ?? '';
        if (label.includes(teamName)) {
          const groupLabelEl = groupCell.querySelector(
            GROUP_LABEL_SELECTOR,
          ) as HTMLElement | null;
          groupLabelEl?.click();
          requestAnimationFrame(() => {
            calendar.updateSize();
            calendar.scrollToResource(userId);
          });
          return;
        }
      }
    },
    [calendar, users],
  );

  const viewType = view?.type ?? ABS_MONTH_VIEW;
  const isMonthView = useMemo(() => viewType === ABS_MONTH_VIEW, [viewType]);
  const isDayView = useMemo(() => viewType === ABS_DAY_VIEW, [viewType]);

  const lockHeaders = useMemo(() => {
    if (!isAbsenceLockHeldStatus(absenceLock.status)) {
      return undefined;
    }
    return {
      'X-Absence-Session-Id': getSessionId(),
    };
  }, [absenceLock.status]);

  const isSelectedUserLocked = useMemo(() => {
    const selectedUserId = selectedSlot?.userId ?? null;
    return (
      !!selectedUserId &&
      isAbsenceLockHeldStatus(absenceLock.status) &&
      absenceLock.lock?.userId === selectedUserId
    );
  }, [absenceLock.lock?.userId, absenceLock.status, selectedSlot?.userId]);

  const book = useCallback(
    async (values: Record<string, unknown>) => {
      const isUpdate = typeof values.absenceId === 'number';
      return bookAbsence(
        {
          ...prepareAbsencePayload(values),
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await invalidateAbsenceQueries();
            await closeEditForm();
            toast.success(
              isUpdate
                ? 'Absence updated successfully'
                : 'Absence created successfully',
            );
            openBalanceHistory();
          },
        },
      );
    },
    [bookAbsence, closeEditForm, lockHeaders, openBalanceHistory, toast],
  );

  const remove = useCallback(
    async (ids: number[]) => {
      return deleteAbsences(
        {
          ids,
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await closeEditForm();
            toast.success('Absence deleted successfully');
            openBalanceHistory();
          },
        },
      );
    },
    [closeEditForm, deleteAbsences, lockHeaders, openBalanceHistory, toast],
  );

  const request = useCallback(
    async (ids: number[]) => {
      return requestAbsences(
        {
          ids,
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await closeAbsenceDetails();
            toast.success('Absence requested successfully');
            openBalanceHistory();
          },
        },
      );
    },
    [
      closeAbsenceDetails,
      lockHeaders,
      openBalanceHistory,
      requestAbsences,
      toast,
    ],
  );

  const withdraw = useCallback(
    async (ids: number[]) => {
      return withdrawAbsences(
        {
          ids,
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async (result) => {
            // Either the days are gone or the decision has moved to
            // somebody else, so the pane is stale either way.
            await closeEditForm();
            // An approved absence is not the user's to take back: it stays
            // put with a request out on it, so reporting it as withdrawn
            // would promise days they have not been given yet.
            toast.success(
              result.requested === 0
                ? 'Absence withdrawn successfully'
                : result.withdrawn === 0
                  ? 'Withdrawal requested successfully'
                  : 'Absence withdrawn; withdrawal requested for the approved days',
            );
            openBalanceHistory();
          },
        },
      );
    },
    [closeEditForm, lockHeaders, openBalanceHistory, toast, withdrawAbsences],
  );

  // Granting a withdrawal either removes the absence or passes the decision to
  // HR, so the pane closes and the calendar is refetched either way.
  const approveWithdrawal = useCallback(
    async (ids: number[]) => {
      return approveWithdrawalAbsences(
        {
          ids,
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await closeAbsenceDetails();
            toast.success('Withdrawal approved successfully');
            openBalanceHistory();
          },
        },
      );
    },
    [
      approveWithdrawalAbsences,
      closeAbsenceDetails,
      lockHeaders,
      openBalanceHistory,
      toast,
    ],
  );

  // Refusing one changes nothing on the calendar, but the pane's actions no
  // longer apply: the request it was offering a decision on is closed.
  const denyWithdrawal = useCallback(
    async (ids: number[]) => {
      return denyWithdrawalAbsences(
        {
          ids,
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await closeAbsenceDetails();
            toast.success('Withdrawal denied successfully');
            openBalanceHistory();
          },
        },
      );
    },
    [
      closeAbsenceDetails,
      denyWithdrawalAbsences,
      lockHeaders,
      openBalanceHistory,
      toast,
    ],
  );

  // The days go, so whatever the pane was showing has gone with them.
  const deny = useCallback(
    async (ids: number[]) => {
      return denyAbsences(
        {
          ids,
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await closeAbsenceDetails();
            toast.success('Absence denied successfully');
            openBalanceHistory();
          },
        },
      );
    },
    [closeAbsenceDetails, denyAbsences, lockHeaders, openBalanceHistory, toast],
  );

  const approve = useCallback(
    async (ids: number[]) => {
      return approveAbsences(
        {
          ids,
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await closeAbsenceDetails();
            toast.success('Absence approved successfully');
            openBalanceHistory();
          },
        },
      );
    },
    [
      approveAbsences,
      closeAbsenceDetails,
      lockHeaders,
      openBalanceHistory,
      toast,
    ],
  );

  const approveHr = useCallback(
    async (ids: number[]) => {
      return approveHrAbsences(
        {
          ids,
          ...(lockHeaders ? { headers: lockHeaders } : {}),
        },
        {
          onSuccess: async () => {
            await closeAbsenceDetails();
            toast.success('Absence approved successfully');
            openBalanceHistory();
          },
        },
      );
    },
    [
      approveHrAbsences,
      closeAbsenceDetails,
      lockHeaders,
      openBalanceHistory,
      toast,
    ],
  );

  const selectedAbsencesById = useMemo<Map<number, Absence>>(
    () => new Map(absences.map((absence: Absence) => [absence.id, absence])),
    [absences],
  );

  const isDifferentAbsencesSelected = useCallback(
    (ids: number[] | null | undefined) => {
      if (!ids || ids.length <= 1) {
        return false;
      }

      let firstType: number | undefined;
      let firstApprovedManager: ManagerApproval | null | undefined;
      let firstApprovedHr: HrApproval | null | undefined;
      for (const id of ids) {
        const obj = selectedAbsencesById.get(id);
        if (!obj) {
          continue;
        }
        if (obj.typeId == null) {
          return true;
        }
        if (firstType === undefined) {
          firstType = obj.typeId;
          firstApprovedManager = obj.approvedManager;
          firstApprovedHr = obj.approvedHr;
        } else if (obj.typeId !== firstType) {
          return true;
        } else if (obj.approvedManager !== firstApprovedManager) {
          return true;
        } else if (obj.approvedHr !== firstApprovedHr) {
          return true;
        }
      }
      return false;
    },
    [selectedAbsencesById],
  );

  const getAbsenceTimeRange = useCallback(
    (slot: TimelineSlot) => {
      let startTime = slot.startTime;
      let endTime = slot.endTime;
      if (
        slot.userId &&
        slot.start &&
        slot.end &&
        !slot.startTime &&
        !slot.endTime
      ) {
        // Check the edited absence first (it's excluded from selectedIds)
        const editedId = slot.editedAbsenceId ?? slot.editedAbsenceIds?.[0];
        const editedAbsence = editedId
          ? absences.find((a) => a.id === editedId)
          : null;

        if (
          editedAbsence &&
          !editedAbsence.allDay &&
          editedAbsence.startTime != null &&
          editedAbsence.endTime != null
        ) {
          startTime = editedAbsence.startTime;
          endTime = editedAbsence.endTime;
        } else {
          // Then check selectedIds for the first hourly event
          const firstHourly = slot.selectedIds
            .map((id) => absences.find((a) => a.id === id))
            .filter(
              (a): a is Absence =>
                !!a && !a.allDay && a.startTime != null && a.endTime != null,
            )
            .sort((a, b) =>
              (a.startTime ?? '').localeCompare(b.startTime ?? ''),
            )[0];

          if (firstHourly) {
            startTime = firstHourly.startTime;
            endTime = firstHourly.endTime;
          } else {
            const userWorktime = getUserWorktimeForDate(
              slot.userId,
              users,
              slot.start,
            );
            if (userWorktime) {
              startTime = userWorktime.start;
              endTime = userWorktime.end;
            }
          }
        }
      }
      return {
        startTime,
        endTime,
      };
    },
    [absences, users],
  );

  const selectedSlotData = useMemo((): SlotData | null => {
    if (!selectedSlot) {
      return null;
    }

    const { selectedIds: selectedAbsencesIds, start, end } = selectedSlot;
    const selectedData = {
      start,
      end,
      typeId: null,
      ...(isDayView && getAbsenceTimeRange(selectedSlot)),
      userId: selectedSlot.userId,
      tentative: false,
      notes: '',
    };

    if (isDayView) {
      if (!selectedSlot.editedAbsenceId) {
        return selectedData;
      }

      const absence =
        absences.find((a) => a.id === selectedSlot.editedAbsenceId) ??
        selectedAbsence?.snapshotAbsences?.find(
          (a) => a.id === selectedSlot.editedAbsenceId,
        );
      if (absence) {
        return {
          ...selectedData,
          typeId: absence.typeId ?? null,
          tentative: absence.absenceTentative ?? false,
          notes: absence.notes ?? '',
        };
      }
      return selectedData;
    }

    const editedId =
      selectedSlot.editedAbsenceId ??
      (selectedSlot.editedAbsenceIds?.length
        ? selectedSlot.editedAbsenceIds[0]
        : null);
    if (editedId) {
      const absence =
        absences.find((a) => a.id === editedId) ??
        selectedAbsence?.snapshotAbsences?.find((a) => a.id === editedId);
      if (absence) {
        const blockSelection = getAbsenceSelectionById(editedId, absences);
        const blockHead = blockSelection
          ? absences.find((a) => a.id === blockSelection.absenceIds[0])
          : null;
        return {
          ...selectedData,
          typeId: absence.typeId ?? null,
          tentative: absence.absenceTentative ?? false,
          notes:
            blockHead?.notes ?? absence.notes ?? selectedAbsence?.notes ?? '',
        };
      }
    }

    if (
      isDifferentAbsencesSelected(selectedAbsencesIds) ||
      !selectedAbsencesIds?.length
    ) {
      return selectedData;
    }

    const selection = getAbsenceSelectionById(selectedAbsencesIds[0], absences);
    if (selection?.absenceIds.length) {
      const absenceId = selection.absenceIds[0];
      const absence =
        absences.find((a) => a.id === absenceId) ??
        selectedAbsence?.snapshotAbsences?.find((a) => a.id === absenceId);
      if (absence) {
        return {
          ...selectedData,
          typeId: absence.typeId ?? null,
          tentative: absence.absenceTentative ?? false,
          notes: absence.notes ?? selectedAbsence?.notes ?? '',
        };
      }
    }
    return selectedData;
  }, [
    absences,
    selectedAbsence,
    selectedSlot,
    isDifferentAbsencesSelected,
    getAbsenceTimeRange,
    isDayView,
  ]);

  useEffect(() => {
    if (!pendingEditSelection) {
      return;
    }

    const hasAllAbsences = pendingEditSelection.absenceIds.every((id) =>
      absences.some((absence) => absence.id === id),
    );

    if (!hasAllAbsences) {
      return;
    }

    const hydratedSelection = hydrateSelectedAbsence(
      pendingEditSelection,
      absences,
    );
    setSelectedAbsence(hydratedSelection);
    setSelectedSlot({
      ...absenceSelectionToSlot(hydratedSelection),
      withdrawing: pendingEditMode === 'withdraw',
    });
    setSidebarMode(pendingEditMode);
    setPendingEditSelection(null);

    requestAnimationFrame(() => {
      expandAndScrollToResource(hydratedSelection.userId);
    });
  }, [
    absences,
    expandAndScrollToResource,
    pendingEditMode,
    pendingEditSelection,
    setSelectedAbsence,
    setSelectedSlot,
    setSidebarMode,
  ]);

  useEffect(() => {
    if (sidebarMode !== 'details' && pendingEditSelection) {
      setPendingEditSelection(null);
    }
  }, [pendingEditSelection, sidebarMode]);

  /**
   * How to work a focused selection out again once more days have loaded.
   *
   * Held rather than recomputed here because the two ways in mean different
   * things by "this absence": a task reference may be pointing at a run being
   * given back, where the balance history always means the block. Each passes
   * its own way of asking.
   */
  const refocusRef = useRef<PendingRefocus | null>(null);

  /**
   * Which focus is the current one.
   *
   * `focusAbsence` hands the pane its selection on the next animation frame,
   * and an animation frame runs before the next paint -- which, during the
   * re-render burst a range change sets off, can be a long way off. Long
   * enough for the days that were missing to arrive and be selected first, and
   * for that late frame to then put the half-selection back. Each focus takes
   * a number and only writes if it is still the one in hand.
   */
  const focusTokenRef = useRef(0);

  /** The window already asked for, so waiting does not ask for it again. */
  const requestedStartRef = useRef<number | null>(null);

  /**
   * Whether the selection on screen is known to be short of the whole absence.
   *
   * The pane opens on what could be seen and fills in when the rest arrives,
   * which leaves a moment where Approve would settle half an absence. The
   * details pane holds its actions until this clears.
   */
  const [isSelectionSettling, setIsSelectionSettling] = useState(false);

  // The days that were off the edge have arrived: replace the half-selection
  // made when the pane opened with the whole absence. Until then the pane
  // shows what it could see, which is better than showing nothing.
  useEffect(() => {
    const pending = refocusRef.current;
    if (!pending) {
      return;
    }

    // Whoever opened this may have clicked elsewhere while the days were on
    // their way; their choice stands and this one is abandoned. Compared
    // against the days focused rather than against the pane, because the pane
    // is handed its selection a frame later -- an empty one here means it has
    // not arrived yet, not that somebody cleared it.
    const movedOn =
      !!selectedAbsence &&
      !selectedAbsence.absenceIds.some((id) => pending.ids.includes(id));
    if (movedOn) {
      refocusRef.current = null;
      setIsSelectionSettling(false);
      return;
    }

    const rebuilt = pending.rebuild(absences);
    if (!rebuilt) {
      // Still loading, or the absence is gone; try again on the next fetch.
      return;
    }

    const fitStart = getFocusWindowStart(rebuilt, visibleRange);
    if (fitStart) {
      // It reached further than one move could show. Asked for once and only
      // once: re-issuing it while the range catches up would put another grid
      // rebuild into a burst that is already over ScrollGrid's layout budget.
      if (requestedStartRef.current !== fitStart.getTime()) {
        requestedStartRef.current = fitStart.getTime();
        calendar.setMonthViewFrom(fitStart);
      }
      return;
    }

    refocusRef.current = null;
    requestedStartRef.current = null;
    setIsSelectionSettling(false);
    // Claims the focus, so the frame still owed from `focusAbsence` knows its
    // half-selection has been overtaken and leaves this one alone.
    focusTokenRef.current += 1;
    setSelectedAbsence(hydrateSelectedAbsence(rebuilt, absences));
  }, [absences, calendar, selectedAbsence, setSelectedAbsence, visibleRange]);

  const focusAbsence = useCallback(
    (
      selection: SelectedAbsence,
      anchorDate: Date,
      rebuild?: RebuildSelection,
    ) => {
      const hydratedSelection = hydrateSelectedAbsence(selection, absences);
      setSuppressSelectionUntil(Date.now() + 500);

      const isHourlyAbsence = selection.startTime !== null;

      if (isDayView || isHourlyAbsence) {
        calendar.setDayView(anchorDate);
      } else {
        // An absence running past the edge of the window is only half in
        // memory, so what is selected here is only half the absence -- and
        // approving it would settle those days and leave the rest hanging.
        // The window moves to hold the whole run, and `rebuild` says how to
        // work the selection out again once the days that were missing
        // arrive.
        // Moved only for an absence the window does not already hold whole.
        // An absence entirely elsewhere counts as one -- it is off both ends
        // of the window -- so this still goes and gets it. What it no longer
        // does is snap to the first of the month for an absence already on
        // screen: every range change rebuilds the grid, and it is the rebuild
        // that outruns ScrollGrid's layout budget.
        const fitStart = getFocusWindowStart(hydratedSelection, visibleRange);

        if (fitStart) {
          requestedStartRef.current = fitStart.getTime();
          calendar.setMonthViewFrom(fitStart);
          // `rebuild` says how to work the selection out again once the days
          // that were missing arrive.
          refocusRef.current = rebuild
            ? { rebuild, ids: hydratedSelection.absenceIds }
            : null;
          setIsSelectionSettling(!!rebuild);
        }
      }

      calendar.scheduleSettleResize();

      const token = ++focusTokenRef.current;
      window.requestAnimationFrame(() => {
        setSidebarMode('details');
        setSelectedUserId(hydratedSelection.userId);
        setSelectedSlot(null);
        // Everything above says which pane to open and for whom, and is worth
        // doing however late it lands. The selection is not: overtaken while
        // this frame waited on a paint the calendar was too busy to reach, it
        // would put the half-absence back over the whole one.
        if (focusTokenRef.current === token) {
          setSelectedAbsence(hydratedSelection);
        }

        // Bring the row on screen the way the edit/withdraw panes do. Deferred
        // one more frame so it lands after the one-shot `scrollTop = 0` the
        // first resource label schedules on its own double rAF.
        window.requestAnimationFrame(() => {
          expandAndScrollToResource(hydratedSelection.userId);
        });
      });
    },
    [
      absences,
      calendar,
      expandAndScrollToResource,
      isDayView,
      setSelectedAbsence,
      setSidebarMode,
      setSuppressSelectionUntil,
      setSelectedSlot,
      setSelectedUserId,
      visibleRange,
    ],
  );

  const openAbsenceInFormPane = useCallback(
    (selection: SelectedAbsence, mode: 'schedule' | 'withdraw') => {
      const hydratedSelection = hydrateSelectedAbsence(selection, absences);
      const anchorDate = new Date(`${hydratedSelection.start}T00:00:00`);

      setPendingEditMode(mode);
      setPendingEditSelection(hydratedSelection);
      setSuppressSelectionUntil(Date.now() + 500);
      setSelectedUserId(hydratedSelection.userId);
      setSelectedAbsence(hydratedSelection);
      setSelectedSlot(null);
      setSidebarMode('details');

      // Whatever was being focused is moot now the user has moved on to
      // editing, and letting it finish would move the calendar under them.
      refocusRef.current = null;
      setIsSelectionSettling(false);

      if (hydratedSelection.startTime !== null) {
        calendar.setDayView(anchorDate);
      } else {
        // Moved only for an absence the window does not already hold whole,
        // the same rule the details pane goes by. Re-anchoring to the first of
        // the month regardless used to be invisible, back when every window
        // started there; now that opening an absence puts the window a week
        // before it, the same call scrolls the timeline out from under the
        // form the moment it opens.
        const fitStart = getFocusWindowStart(hydratedSelection, visibleRange);
        if (fitStart) {
          calendar.setMonthViewFrom(fitStart);
        }
      }

      calendar.scheduleSettleResize();

      requestAnimationFrame(() => {
        expandAndScrollToResource(hydratedSelection.userId);
      });
    },
    [
      absences,
      calendar,
      expandAndScrollToResource,
      setSelectedAbsence,
      setSelectedSlot,
      setSidebarMode,
      setSuppressSelectionUntil,
      setSelectedUserId,
      visibleRange,
    ],
  );

  const editAbsence = useCallback(
    (selection: SelectedAbsence) =>
      openAbsenceInFormPane(selection, 'schedule'),
    [openAbsenceInFormPane],
  );

  const withdrawAbsence = useCallback(
    (selection: SelectedAbsence) =>
      openAbsenceInFormPane(selection, 'withdraw'),
    [openAbsenceInFormPane],
  );

  const timeline = {
    absenceLock,
    isSelectionSettling,
    calendar,
    closeEditForm,
    closeAbsenceDetails,
    view,
    selectedAbsence,
    setSelectedAbsence,
    selectedSlot,
    setSelectedSlot,
    sidebarMode,
    setSidebarMode,
    selectedUserId,
    setSelectedUserId,
    attachCalendar,
    ...restOfContext,
    ...restAbsencesQuery,
    absences,
    holidays,
    users,
    vacationBalances,
    isUsersFetching,
    isUsersFetched,
    visibleRange,
    book,
    selectedSlotData,
    remove,
    request,
    withdraw,
    approveWithdrawal,
    denyWithdrawal,
    deny,
    approve,
    approveHr,
    isMonthView,
    isDayView,
    isSelectedUserLocked,
    showLegend,
    openLegend,
    closeLegend,
    showBalanceHistory,
    openBalanceHistory,
    closeBalanceHistory,
    expandAndScrollToResource,
    editAbsence,
    withdrawAbsence,
    focusAbsence,
  };
  return timeline;
}
